/**
 * Money across deals, per currency — never silently across them.
 *
 * A deal's amounts are held in its own `currency` (catalogue field, ISO 4217) and
 * are never converted: the platform carries no FX rate and invents none. So a
 * total over several deals has to be a total PER currency. $40M of USD loans and
 * C$10M of CAD loans is not "$50M" of anything.
 *
 * A book that is entirely one currency reads exactly as it always did. A mixed
 * book is shown as per-currency subtotals, with an explicit `mixed` marker the
 * caller can badge ("mixed currencies"), never as one added-up number.
 *
 * The server mirror is `backend/app/services/currency_totals.py`.
 *
 * Callers: the Requested tile in `PipelineIntelligence.tsx` and the stage rail header
 * in `StageRail.tsx`, which sum `requestedCommitment(loan)` through this.
 */
import type { Loan } from '@/types'

/** What a deal is in until a package or a person names a currency. */
export const DEFAULT_CURRENCY = 'USD'

/** The currency a deal's money is held in: its own, else the default (which is
 *  also what every deal created before the field existed always was). */
export function currencyOf(loan: Pick<Loan, 'customFields'> | null | undefined): string {
  const raw = loan?.customFields?.currency
  const code = typeof raw === 'string' ? raw.trim().toUpperCase() : ''
  return code || DEFAULT_CURRENCY
}

export type CurrencySubtotal = { currency: string; amount: number; count: number }

export type MoneyByCurrency = {
  /** One subtotal per currency, the default currency first and the rest alphabetical. */
  totals: CurrencySubtotal[]
  currencies: string[]
  /** More than one currency is present: there is NO single total, and none is offered. */
  mixed: boolean
}

/**
 * Sum `amountOf(loan)` per currency. A deal with no amount adds nothing and is not
 * counted — a total never pretends to cover a deal that states no figure.
 */
export function sumMoneyByCurrency<T extends Pick<Loan, 'customFields'>>(
  loans: ReadonlyArray<T>,
  amountOf: (loan: T) => number | null | undefined,
): MoneyByCurrency {
  const buckets = new Map<string, CurrencySubtotal>()
  for (const loan of loans) {
    const amount = amountOf(loan)
    if (amount == null || typeof amount !== 'number' || Number.isNaN(amount)) continue
    const currency = currencyOf(loan)
    const bucket = buckets.get(currency) ?? { currency, amount: 0, count: 0 }
    bucket.amount += amount
    bucket.count += 1
    buckets.set(currency, bucket)
  }
  const totals = [...buckets.values()].sort((a, b) => {
    const lead = Number(a.currency !== DEFAULT_CURRENCY) - Number(b.currency !== DEFAULT_CURRENCY)
    return lead || a.currency.localeCompare(b.currency)
  })
  return { totals, currencies: totals.map((t) => t.currency), mixed: totals.length > 1 }
}

/**
 * A money figure with its ISO code beside it: "$30.00M USD", "$12.50M CAD". The
 * code is printed for every currency, USD included, so no figure on a screen
 * that can hold more than one is left to be assumed.
 */
export function moneyWithCode(text: string, currency: string | null | undefined): string {
  if (!text || text === '—') return text
  return `${text} ${(currency || DEFAULT_CURRENCY).toUpperCase()}`
}

/**
 * The totals as a reader sees them. A single-currency book is one figure, as it
 * always was; a mixed book lists each currency's own subtotal, joined with "+"
 * only to read as a list — never added.
 */
export function formatMoneyByCurrency(
  summary: MoneyByCurrency,
  format: (amount: number) => string,
): string {
  if (summary.totals.length === 0) return '—'
  if (!summary.mixed) {
    const [only] = summary.totals
    return only.currency === DEFAULT_CURRENCY ? format(only.amount) : moneyWithCode(format(only.amount), only.currency)
  }
  return summary.totals.map((t) => moneyWithCode(format(t.amount), t.currency)).join(' + ')
}
